// Számlagenerálás-dialógus (admin, invoices.manage) — a KÉSZ `POST /invoices/generate` endpoint operábilis
// triggere a számla-lista toolbarjából. A UI-konvenció: modal a felhívó műveletnek (mint a kiállítás/sztornó).
// Tartalom: hónap-léptető (a forecast hónap-választó mintája) + pricing-type checkboxok (üres=mind) + „Szimuláció
// (dry-run)" kapcsoló (alapból BE — biztonságos). „Generálás" → a mutáció; az eredmény (dueAssignments/groups/
// invoices/items + skipped[]) olvashatóan; dry-runnál „ennyi jönne létre", éles futásnál toast + lista-invalidálás.
import { type InvoiceGenerateResultSchema } from '@atlas/contracts';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { DomainBadge } from '@/components/shared/domain-badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { currentMonth, formatMonthLabel, shiftMonth } from '@/lib/month';
import { api } from '@/lib/api';

type GenerateResult = z.infer<typeof InvoiceGenerateResultSchema>;

/** A 4 pricing-type (a contract enumja 1:1) — üres kiválasztás = mind (a BFF alapértelmezése). */
const PRICING_TYPES = ['flat', 'tiered', 'base_tiered', 'commission'] as const;
type PricingType = (typeof PRICING_TYPES)[number];

/** Egy összegző statisztika-csempe (címke + érték) az eredmény-panelhez. */
function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="border-border bg-muted/30 rounded-lg border px-3 py-2">
      <Text variant="secondary" as="div">
        {label}
      </Text>
      <Text variant="itemTitle" as="div" className="tabular-nums">
        {value}
      </Text>
    </div>
  );
}

/** Az eredmény-panel (dry-run vagy éles futás után). A skipped[] olvashatóan (assignment-id + tétel + jelvényes ok). */
function ResultPanel({
  result,
  t,
  month,
  onViewSkips,
}: {
  result: GenerateResult;
  t: TFunction;
  month: string;
  onViewSkips?: ((month: string) => void) | undefined;
}): JSX.Element {
  return (
    <div className="space-y-3" data-testid="generate-result">
      <Text variant="secondary" as="p">
        {result.dryRun ? t('invoices.generate.dryRunResult') : t('invoices.generate.liveResult')}
      </Text>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label={t('invoices.generate.dueAssignments')} value={result.dueAssignments} />
        <Stat label={t('invoices.generate.groups')} value={result.groups} />
        <Stat label={t('invoices.generate.invoices')} value={result.invoices} />
        {/* A MÁR NYITOTT piszkozathoz FŰZÖTT számlák — a napi futás alapesete a hónap 2-a után. */}
        <Stat label={t('invoices.generate.appended')} value={result.appended} />
        <Stat label={t('invoices.generate.items')} value={result.items} />
      </div>
      {result.skipped.length > 0 && (
        <div className="space-y-1.5">
          <Text variant="secondary" as="div" className="font-medium">
            {t('invoices.generate.skipped', { count: result.skipped.length })}
          </Text>
          {/* OKONKÉNT CSOPORTOSÍTVA, darabszámmal — nem soronként.
              MIÉRT: a próba-futás kérdése „mennyi és MIÉRT", és erre egy ok-szerinti bontás felel. Soronként
              a lista a nyers `assignmentId`-t mutatta (a válasz mást nem is hordoz), amiből a felhasználó nem
              tudja megállapítani, MIT kell pótolnia — pedig épp ezért van a panel. Mérve a lokál szeptemberi
              futáson: 874 sornyi UUID egy 12rem magas görgetődobozban.
              A SORONKÉNTI, NEVESÍTETT kép az ÉLES futás után a kihagyás-naplóban él (lásd a lenti linket);
              a próba-futás nem is naplóz, tehát ott ez az egyetlen olvasat. */}
          <ul className="border-border space-y-1 rounded-lg border p-2">
            {[
              ...result.skipped.reduce(
                (acc, s) => acc.set(s.reason, (acc.get(s.reason) ?? 0) + 1),
                new Map<string, number>(),
              ),
            ]
              .sort((a, b) => b[1] - a[1])
              .map(([reason, count]) => (
                <li key={reason} className="flex items-center justify-between gap-3 text-sm">
                  <DomainBadge
                    kind="generationSkipReason"
                    value={reason}
                    label={t(`invoices.generate.skipReason.${reason}`, { defaultValue: reason })}
                  />
                  <span className="text-muted-foreground shrink-0 tabular-nums">
                    {t('invoices.generate.skippedCount', { count })}
                  </span>
                </li>
              ))}
          </ul>
          {/* NEM-próba futás + volt kihagyás → a napló-fülre mutató link (a runId-vel összefűzhető sorok).
              GOMB, NEM route-Link: a dialógus a hívó lapon MARAD (nincs teljes navigáció) — a szülő a
              `onViewSkips`-en át vált fület + zárja a dialógust ugyanazon az oldalon. */}
          {!result.dryRun && onViewSkips && (
            <Text variant="secondary" as="p" className="text-xs">
              {t('invoices.generate.skipsHint')}{' '}
              <button
                type="button"
                onClick={() => onViewSkips(month)}
                className="text-brand hover:underline"
              >
                {t('invoices.generate.skipsHintLink')}
              </button>
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A számlagenerálás-dialógus. A `open`/`onOpenChange` a lista-oldalról vezérli; a sikeres ÉLES futás után
 * lista-invalidálás (az új draftok megjelennek) + siker-toast. A dialógus záráskor visszaáll (eredmény törlődik).
 */
export function InvoiceGenerateDialog({
  open,
  onOpenChange,
  onViewSkips,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ÉLES futás + kihagyás után a „Generálási kihagyások" fülre ugrás — a hívó (a lista-oldal) zárja a
   *  dialógust és vált fület UGYANAZON az oldalon (nincs teljes navigáció). Nélküle a hint-sor nem jelenik meg. */
  onViewSkips?: ((month: string) => void) | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState<string>(currentMonth());
  const [types, setTypes] = useState<Set<PricingType>>(new Set());
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<GenerateResult | null>(null);

  const reset = (): void => {
    setMonth(currentMonth());
    setTypes(new Set());
    setDryRun(true);
    setResult(null);
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const toggleType = (type: PricingType): void => {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: () =>
      api.invoicesGenerate.mutation({
        body: {
          month,
          types: types.size > 0 ? [...types] : undefined,
          dryRun,
        },
      }),
    onSuccess: (res) => {
      // A PRÓBA-futás szinkron: az eredmény azonnal itt van, meg is jelenítjük.
      if (res.status === 200) {
        setResult(res.body);
      } else if (res.status === 202) {
        // AZ ÉLES FUTÁS HÁTTÉRBEN MEGY (percekig tarthat — az Odin percenkénti fojtását kivárva). A válasz
        // csak annyit mond, hogy elindult; a VÉGÉRŐL a kezdeményező értesítést kap, az eredmény pedig a
        // számla-listában és a kihagyás-naplóban jelenik meg. A dialógust ezért be is zárjuk.
        toast.success(t('toast:invoices.generationStarted', { month: res.body.month }));
        onOpenChange(false);
        // A listák NEM most frissülnek (a futás még tart) — az értesítés érkezésekor a felhasználó
        // úgyis újratölti/visszatér. Egy azonnali invalidálás csak egy üres kört jelentene.
      } else if (res.status === 409) {
        toast.error(t('toast:invoices.generationBusy'));
      } else if (res.status === 503) {
        toast.error(t('toast:invoices.generationUnavailable'));
      } else if (res.status === 403) {
        toast.error(t('toast:invoices.forbidden'));
      } else if (res.status === 422) {
        toast.error(t('toast:invoices.validation'));
      } else {
        toast.error(t('toast:invoices.error'));
      }
    },
    onError: () => toast.error(t('toast:invoices.error')),
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invoices.generate.title')}</DialogTitle>
          <DialogDescription>{t('invoices.generate.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {/* Hónap-léptető */}
          <div className="space-y-1.5">
            <Text variant="secondary" as="label" className="block">
              {t('invoices.generate.month')}
            </Text>
            <div className="border-border input-glass flex h-9 w-fit items-center rounded-lg border">
              <button
                type="button"
                aria-label={t('invoices.generate.prevMonth')}
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                className="text-muted-foreground hover:text-foreground flex h-full items-center px-2 focus-visible:outline-none"
              >
                <ChevronLeftIcon className="size-4" />
              </button>
              <span className="text-foreground min-w-36 select-none text-center font-medium tabular-nums">
                {formatMonthLabel(month, i18n.language)}
              </span>
              <button
                type="button"
                aria-label={t('invoices.generate.nextMonth')}
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                className="text-muted-foreground hover:text-foreground flex h-full items-center px-2 focus-visible:outline-none"
              >
                <ChevronRightIcon className="size-4" />
              </button>
            </div>
          </div>

          {/* Pricing-type checkboxok (üres = mind) */}
          <div className="space-y-1.5">
            <Text variant="secondary" as="span" className="block">
              {t('invoices.generate.types')}
            </Text>
            <div className="grid grid-cols-2 gap-1.5">
              {PRICING_TYPES.map((type) => (
                <label key={type} className="text-md flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={types.has(type)}
                    onCheckedChange={() => toggleType(type)}
                    aria-label={t(`invoices.generate.pricingType.${type}`)}
                  />
                  <span className="text-foreground">
                    {t(`invoices.generate.pricingType.${type}`)}
                  </span>
                </label>
              ))}
            </div>
            <Text variant="secondary" as="p" className="text-xs">
              {t('invoices.generate.typesHint')}
            </Text>
          </div>

          {/* Dry-run kapcsoló */}
          <label className="flex cursor-pointer items-start justify-between gap-4">
            <div className="min-w-0">
              <Text variant="body" as="div" className="font-medium">
                {t('invoices.generate.dryRun')}
              </Text>
              <Text variant="secondary" as="div" className="text-xs">
                {t('invoices.generate.dryRunHint')}
              </Text>
            </div>
            <Switch
              checked={dryRun}
              onCheckedChange={(v) => setDryRun(v === true)}
              aria-label={t('invoices.generate.dryRun')}
            />
          </label>

          {result && (
            <ResultPanel
              result={result}
              t={t}
              month={month}
              onViewSkips={
                onViewSkips
                  ? (m) => {
                      onViewSkips(m);
                      handleOpenChange(false);
                    }
                  : undefined
              }
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('invoices.generate.close')}
          </Button>
          <Button variant="brand" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending
              ? t('invoices.generate.generating')
              : dryRun
                ? t('invoices.generate.simulate')
                : t('invoices.generate.generate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
